import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { IkDomElement, IkDomNode } from "../../../Admin/kReusableCoreTS/kHtmlParserCore";
import { kHtmlSafeCore } from "../../../Admin/kReusableCoreTS/kHtmlSafeCore";
import { AppAssistBlock, AppAssistSpan } from "./AppAssistRules";

// AppAssistExport - the rules for taking a table or a chart OUT of an AI answer (owner-approved,
// 2026-09-27): "Copy table" puts the table on the clipboard as tab-separated text (pastes straight into a
// spreadsheet); "Save chart" downloads the chart as an .svg file; tapping a bar shows its `<title>` under
// the chart; and the names a screen reader gives a chart or a table the model did not name.
//
// ⛔ WHAT IS READ IS OUR OWN, NEVER THE MODEL'S MARKUP: the rows come from the CLEANED tree
// (`kHtmlSafeCore.Clean`'s output) or the pipe table's parsed runs, and the chart that is saved is the
// node the panel BUILT from that cleaned tree. Nothing here parses or emits markup; text stays text.
//
// ⚠ Pure: no DOM, no `window`. The browser half is `AppAssistTools`; the panel draws.

export class AppAssistExport {
    /** On a chart mark that has a `<title>`: its number in the panel's list of marks for this paint. */
    public static readonly MarkAttr: string = "data-wac-mark";
    /** "1" on the mark whose value is shown under its chart, "0" once hidden. */
    public static readonly OnAttr: string = "data-wac-on";
    /** The log buttons (`AppAssistView.ActAttr` values). Their `IndexAttr` is the block's number, not an entry's. */
    public static readonly CopyAct: string = "copytable";
    public static readonly SaveAct: string = "savechart";
    /** What a screen reader calls a chart with no `<title>` / a table with no `<caption>`. */
    public static readonly ChartName: string = "Chart";
    public static readonly TableName: string = "Table";

    /**
     * The computed properties written onto each element of a saved chart, so the file looks as it did in
     * the panel wherever it is opened - the palette classes (`.c1`..`.c6`, `muted`) and `currentColor`
     * text are the app's stylesheet, which does not travel with the file.
     */
    public static readonly PaintProps: string[] = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-opacity",
        "stroke-dasharray", "opacity", "font-family", "font-size", "font-weight", "font-style"];

    /**
     * A tab, CR, LF or Unicode line/paragraph separator (U+2028/U+2029), in runs. ⛔ BUILT FROM A STRING
     * WITH ESCAPED ESCAPES ON PURPOSE: the edit tooling turned a regex LITERAL's line-separator escape into
     * the raw character, which ends a JavaScript line - an "unterminated regular expression" at best, a
     * silently different pattern at worst (the `HeaderSafe` hazard in the project CLAUDE.md, again).
     */
    public static readonly Breaks: RegExp = new RegExp("[\\t\\r\\n\\u2028\\u2029]+", "g");

    /** One cell for TSV: a tab or a line break inside it would start a new cell or row, so each run is a space. */
    public static CellText = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(Text).replace(AppAssistExport.Breaks, " ").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.CellText");
            return "";
        }
    };

    /** Rows of cells as tab-separated text: a tab between cells, a line break between rows, header first. */
    public static TsvOf = (Rows: string[][]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Lines: string[] = [];
            for (const Row of Rows) {
                const Cells: string[] = [];
                for (const Cell of Row) { Cells.push(AppAssistExport.CellText(Cell)); }
                Lines.push(Cells.join("\t"));
            }
            return Lines.join("\n");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.TsvOf");
            return "";
        }
    };

    /** A pipe table's rows: the header, then each body row - every cell its runs' words, marks dropped. */
    public static RowsOfBlock = (Block: AppAssistBlock): string[][] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[][] = [AppAssistExport.CellsOf(Block.Head)];
            for (const Row of Block.Rows) { Out.push(AppAssistExport.CellsOf(Row)); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.RowsOfBlock");
            return [];
        }
    };

    private static CellsOf = (Cells: AppAssistSpan[][]): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            for (const Cell of Cells) {
                let Words: string = "";
                for (const Span of Cell) { Words = Words + nz(Span.Text); }
                Out.push(Words);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.CellsOf");
            return [];
        }
    };

    /**
     * A cleaned `<table>`'s rows: every `<tr>` of its `<thead>` first, then the others in document order;
     * each `<th>`/`<td>` its text. ⚠ A nested table is not looked into - its rows are not this table's.
     */
    public static RowsOfTable = (Table: IkDomElement): string[][] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Head: IkDomElement[] = [];
            const Rest: IkDomElement[] = [];
            AppAssistExport.RowsUnder(Table, false, Head, Rest);
            const Out: string[][] = [];
            for (const Row of Head.concat(Rest)) {
                const Cells: string[] = [];
                for (const Kid of AppAssistExport.ElementsOf(Row)) {
                    const Tag: string = kHtmlSafeCore.TagOf(Kid);
                    if (Tag === "th" || Tag === "td") { Cells.push(AppAssistExport.TextOf(Kid)); }
                }
                Out.push(Cells);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.RowsOfTable");
            return [];
        }
    };

    /** Collects the `<tr>`s under `El` (not inside a nested table), those inside a `<thead>` into `Head`. */
    private static RowsUnder = (El: IkDomElement, InHead: boolean, Head: IkDomElement[], Rest: IkDomElement[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Kid of AppAssistExport.ElementsOf(El)) {
                const Tag: string = kHtmlSafeCore.TagOf(Kid);
                if (Tag === "table") { continue; }
                if (Tag === "tr" && InHead === true) { Head.push(Kid); continue; }
                if (Tag === "tr") { Rest.push(Kid); continue; }
                AppAssistExport.RowsUnder(Kid, InHead === true || Tag === "thead", Head, Rest);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.RowsUnder");
        }
    };

    /** The element children of a cleaned element, in order. */
    private static ElementsOf = (El: IkDomElement): IkDomElement[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: IkDomElement[] = [];
            for (const Kid of El.childNodes) { if (Kid.nodeType === 1) { Out.push(Kid as IkDomElement); } }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.ElementsOf");
            return [];
        }
    };

    /** Every text node's words under `Node`, in order. `<br>` reads as a space. */
    public static TextOf = (Node: IkDomNode): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Node.nodeType === 3) { return nz(Node.nodeValue); }
            if (Node.nodeType !== 1) { return ""; }
            const El: IkDomElement = Node as IkDomElement;
            if (kHtmlSafeCore.TagOf(El) === "br") { return " "; }
            let Out: string = "";
            for (const Kid of El.childNodes) { Out = Out + AppAssistExport.TextOf(Kid); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.TextOf");
            return "";
        }
    };

    /** The text of `El`'s own `<title>` child (not a descendant's), spaces collapsed; "" when it has none. */
    public static TitleOf = (El: IkDomElement): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            for (const Kid of AppAssistExport.ElementsOf(El)) {
                if (kHtmlSafeCore.TagOf(Kid) === "title") { return AppAssistExport.TextOf(Kid).replace(/\s+/g, " ").trim(); }
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.TitleOf");
            return "";
        }
    };

    /** What a chart is called when the model gave it no `aria-label`: its own `<title>`, else "Chart". */
    public static ChartLabel = (Svg: IkDomElement): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistExport.ChartName; }
        try {
            const Title: string = AppAssistExport.TitleOf(Svg);
            if (Title.length > 0) { return Title; }
            return AppAssistExport.ChartName;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.ChartLabel");
            return AppAssistExport.ChartName;
        }
    };

    /** Whether a cleaned table has its own `<caption>` - which already names it for a screen reader. */
    public static HasCaption = (Table: IkDomElement): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            for (const Kid of AppAssistExport.ElementsOf(Table)) { if (kHtmlSafeCore.TagOf(Kid) === "caption") { return true; } }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.HasCaption");
            return false;
        }
    };

    /**
     * A chart mark worth tapping: an SVG element INSIDE the chart (never the chart's own `<svg>`, whose
     * `<title>` names the chart) that has its own `<title>` - the model's per-bar value ("Jun: 9 messages").
     */
    public static IsMark = (El: IkDomElement, IsOuterSvg: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (IsOuterSvg === true) { return false; }
            const Tag: string = kHtmlSafeCore.TagOf(El);
            if (Tag === "title" || Tag === "desc" || Tag === "defs") { return false; }
            return AppAssistExport.TitleOf(El).length > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.IsMark");
            return false;
        }
    };

    /**
     * "<contact>-chart-<n>.svg". The contact's name keeps its letters (Björn stays Björn); anything a file
     * system refuses, spaces and dots become one dash; a raw id or no name at all is "chat".
     */
    public static FileName = (Contact: string, IsRaw: boolean, N: number): string => {
        if (getCoreUtils().KErrorMode === true) { return "chat-chart.svg"; }
        try {
            let Stem: string = nz(Contact).replace(/[\\/:*?"<>|\u0000-\u001f\u007f\s.]+/g, "-")
                .replace(/-+/g, "-").replace(/^-+|-+$/g, "").substring(0, 40).replace(/-+$/g, "");
            if (IsRaw === true || Stem.length === 0) { Stem = "chat"; }
            let Seq: number = Math.floor(N);
            if (isNaN(Seq) === true || Seq < 1) { Seq = 1; }
            return Stem + "-chart-" + String(Seq) + ".svg";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.FileName");
            return "chat-chart.svg";
        }
    };

    /**
     * A `style` attribute from computed property values: `name:value;` for each non-empty one.
     * ⛔ A `url(...)` is kept ONLY as a reference to an id in the same file (`url("#…")` / `url(#…)` - a
     * gradient the cleaner already prefixed); anything else a `url()` could reach is dropped, as is a value
     * that could end the declaration early.
     */
    public static StyleOf = (Names: string[], Values: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (let At: number = 0; At < Names.length && At < Values.length; At = At + 1) {
                const Value: string = nz(Values[At]).trim();
                if (Value.length === 0 || /[;{}<>\\]/.test(Value) === true) { continue; }
                if (/url\(/i.test(Value) === true && /^url\(\s*"?#[A-Za-z0-9_-]+"?\s*\)$/i.test(Value) !== true) { continue; }
                Out.push(Names[At] + ":" + Value);
            }
            return Out.join(";");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.StyleOf");
            return "";
        }
    };

    /** Whether a computed colour paints nothing ("transparent", or an rgba/hsla with alpha 0). */
    public static IsClear = (Colour: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const Text: string = nz(Colour).replace(/\s+/g, "").toLowerCase();
            if (Text.length === 0 || Text === "transparent" || Text === "none") { return true; }
            return /^(rgba|hsla)\(.*,0(\.0+)?\)$/.test(Text) === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.IsClear");
            return true;
        }
    };

    /**
     * Where the saved chart's backdrop rectangle goes - x, y, width, height - so the file keeps the panel's
     * background (chart text is ink, light on a dark panel; without a backdrop it would vanish on a white
     * viewer). The `viewBox`'s own box when it reads as four numbers, else the whole canvas.
     */
    public static BackdropOf = (ViewBox: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return ["0", "0", "100%", "100%"]; }
        try {
            const Parts: string[] = nz(ViewBox).trim().split(/[\s,]+/);
            if (Parts.length !== 4) { return ["0", "0", "100%", "100%"]; }
            for (const One of Parts) {
                if (/^-?(\d+\.?\d*|\.\d+)$/.test(One) !== true) { return ["0", "0", "100%", "100%"]; }
            }
            return Parts;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistExport.BackdropOf");
            return ["0", "0", "100%", "100%"];
        }
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static CopiedNote = (): string => {
        return "Copied - paste it into a spreadsheet.";
    };

    /**
     * ⚠ INK, NOT THE ERROR HANDLER: the browser refusing the clipboard (no permission, no secure context,
     * an old browser) is its answer, not our fault - and the owner can do it by hand.
     * DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw.
     */
    public static CopyRefusedNote = (): string => {
        return "Couldn't copy - select the table and copy it by hand.";
    };

    /** DOCUMENTED EXCEPTION: builds a sentence from a file name; cannot throw. */
    public static SavedNote = (Name: string): string => {
        return "Saved as " + nz(Name) + " - see your downloads.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static SaveRefusedNote = (): string => {
        return "This browser can't save the chart as a file.";
    };

    /** The Copy button's words. DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static CopyLabel = (): string => { return "Copy table"; };
    /** What a screen reader says for it. DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static CopyAria = (): string => { return "Copy this table, to paste into a spreadsheet"; };
    /** The Save button's words. DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static SaveLabel = (): string => { return "Save chart"; };
    /** What a screen reader says for it. DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static SaveAria = (): string => { return "Save this chart as an SVG file"; };
}
